import { CGProduct } from '../../types/shopify';
import CardType from './steps/CardType';
import CardValue from './steps/CardValue';
import CardRecipient from './steps/CardRecipient';
import CardMessage from './steps/CardMessage';
import CardDesign from './steps/CardDesign';
import CardDelivery from './steps/CardDelivery';
import CardConfirm from './steps/CardConfirm';
import { CGCardConfigFormValues } from 'types/cardForm';
import { STEPS, PRODUCT_TYPE, PRODUCT_HANDLE, CARD_MEDIUM_DIGITAL, CARD_MEDIUM_PHYSICAL, RECIPIENT_COUNT_MULTIPLE, RECIPIENT_COUNT_SINGLE, RECIPIENT_TYPE_SELF } from 'utilities/constants';
import mastercardLogo from 'assets/cardCustomisation/cardLogo/mastercardLogo.png';
import eftposLogo from 'assets/cardCustomisation/cardLogo/eftposLogo.png';

const STEP_COMPONENTS = {
  [STEPS.TYPE]: CardType,
  [STEPS.VALUE]: CardValue,
  [STEPS.RECIPIENT]: CardRecipient,
  [STEPS.MESSAGE]: CardMessage,
  [STEPS.DESIGN]: CardDesign,
  [STEPS.SEND]: CardDelivery,
  [STEPS.CONFIRM]: CardConfirm,
};

// These collection handles contain predesigned cards that are presented as options in the design step.
export const designedMastercardCollectionHandle = 'custom-mastercards';
export const designedEftposCollectionHandle = 'designed-eftpos-cards';
export const designedCardDotGiftCollectionHandle = 'designed-carddotgift-smart-cards';

export const customMasterCardDesignHandle = 'mastercard-custom';
export const customCardDotGiftCardDesignHandle = 'carddotgift-custom';
export const customEftposCardDesignHandle = 'eftpos-custom';

export const isOpenLoopCard = (product: CGProduct): boolean => product.productType === PRODUCT_TYPE.OPEN_LOOP_CARD;
export const isCustomDesignCardByHandle = (productHandle: string): boolean => productHandle.includes('-custom');
export const isPredesignedCardByHandle = (productHandle: string): boolean => productHandle.includes('-designed-');
export const isPredesignedCard = (product: CGProduct): boolean => isPredesignedCardByHandle(product.handle);
// export const isPredesignedMasterCard = (product: CGProduct): boolean => product.handle.includes('mastercard-designed');
// export const isPredesignedCardDotGiftCard = (product: CGProduct): boolean => product.handle.includes('carddotgift-designed');
export const isMultichoiceCard = (product: CGProduct): boolean => product.productType === PRODUCT_TYPE.MULTICHOICE_CARD;
export const isCustomMasterDesignCard = (product: CGProduct): boolean => product.handle === customMasterCardDesignHandle;
export const isCustomCardDotGiftDesignCard = (product: CGProduct): boolean => product.handle === customCardDotGiftCardDesignHandle;
export const isCustomEftposDesignCard = (product: CGProduct): boolean => product.handle === customEftposCardDesignHandle;
export const isCardDotGiftCard = (product: CGProduct): boolean => product.productType === PRODUCT_TYPE.CARD_DOT_GIFT_CARD;
export const isRootCard = (product: CGProduct): boolean => product.handle === PRODUCT_HANDLE.CARD_GIFT || product.handle === PRODUCT_HANDLE.EFTPOS || product.handle === PRODUCT_HANDLE.MASTER_CARD;

// TODO - switch to be based on a metafield, rather than handle...
export const canShowDesignOptionsByProduct = (product: CGProduct) => isOpenLoopCard(product);

// Switching to have design options only shown for the root cards...
export const canShowDesignOptions = (product: CGProduct, values: CGCardConfigFormValues) =>
  (values.cardMedium === CARD_MEDIUM_PHYSICAL && isRootCard(product)) || (isRootCard(product) && isCardDotGiftCard(product));

// TODO - Does the following line need to check for root eftpos and root card.gift cards too?
export const canCustomiseDesign = (product: CGProduct): boolean => isRootCard(product) || isCustomDesignCardByHandle(product.handle);
export const canCustomiseWithLogo = false;
export const hideContinueButton = (step: number, product: CGProduct, values: CGCardConfigFormValues) => step === 0 && canShowDesignOptions(product, values);

export const availableSteps = (productType: string | undefined) => {
  switch (productType) {
    case PRODUCT_TYPE.CARD_DOT_GIFT_CARD:
    case PRODUCT_TYPE.OPEN_LOOP_CARD:
      return [STEPS.TYPE, STEPS.DESIGN, STEPS.VALUE, STEPS.RECIPIENT, STEPS.MESSAGE, STEPS.CONFIRM, STEPS.SEND];
    default:
      return [STEPS.TYPE, STEPS.VALUE, STEPS.RECIPIENT, STEPS.MESSAGE, STEPS.CONFIRM, STEPS.SEND];
  }
};

export const availableComponents = (productType: string | undefined) => availableSteps(productType).map((step) => STEP_COMPONENTS[step]);

export const getInitialDisableSteps = () => [STEPS.RECIPIENT, STEPS.CONFIRM];

//when to disbale/ enable steps
export const whenToDisableSteps = (updateDisableStep: (step: string, shouldDisable: boolean) => void, values: any, product: CGProduct) => {
  // console.debug({
  //   isCustomMasterDesignCard: isCustomMasterDesignCard(product),
  //   isCustomCardDotGiftDesignCard: isCustomCardDotGiftDesignCard(product),
  //   isCustomEftposDesignCard: isCustomEftposDesignCard(product),
  //   isOpenLoopCard: isOpenLoopCard(product),
  //   cardMedium: values.cardMedium,
  //   isPredesignedCard: isPredesignedCard(product),
  //   isCardDotGiftCard: isCardDotGiftCard(product),
  // });
  // when to disable design step
  updateDisableStep(
    STEPS.DESIGN,
    // disable if the card IS NOT open loop or cardDotGift - quick win
    (!isOpenLoopCard(product) && !isCardDotGiftCard(product)) ||
      // Or disable if the card IS OpenLoop and the card medium is digital
      (isOpenLoopCard(product) && values.cardMedium === CARD_MEDIUM_DIGITAL),
    // NOTE - PreDesigned cards, or custom cards that are visited directly
    // still see the design step

    // Not a custom design card...
    // !isCustomMasterDesignCard(product) &&
    //   !isCustomCardDotGiftDesignCard(product) &&
    //   !isCustomEftposDesignCard(product) &&
    // Not open loop
    // (!isOpenLoopCard(product) ||
    //   // or Card medium is digital?
    //   values.cardMedium === CARD_MEDIUM_DIGITAL ||
    //   // or is a designed card?
    //   isPredesignedCard(product)
    //   ) &&
    // !isCardDotGiftCard(product),
  );

  //when to disable recipient step
  updateDisableStep(STEPS.RECIPIENT, values.cardMedium === CARD_MEDIUM_PHYSICAL && values.recipientCount !== RECIPIENT_COUNT_MULTIPLE);

  // when to disable message step
  updateDisableStep(
    STEPS.MESSAGE,
    (values.cardMedium === CARD_MEDIUM_PHYSICAL && values.recipientCount === RECIPIENT_COUNT_MULTIPLE) || (values.cardMedium === CARD_MEDIUM_DIGITAL && values.recipientType === RECIPIENT_TYPE_SELF),
  );

  // when to disable confirm step
  updateDisableStep(STEPS.CONFIRM, !values.recipientCount || values.recipientCount === RECIPIENT_COUNT_SINGLE);

  // when to disable send step
  updateDisableStep(STEPS.SEND, values.cardMedium === CARD_MEDIUM_PHYSICAL || (values.cardMedium === CARD_MEDIUM_DIGITAL && values.recipientType === RECIPIENT_TYPE_SELF));
};

export const canUploadVideo = (values: any, productType: string | undefined) => {
  // https://iegroup.atlassian.net/browse/TCN-516
  // Single, send to self
  // - Not able to personalise message
  // - Not able to upload video
  if (values.cardMedium !== CARD_MEDIUM_DIGITAL) return false;
  if (values.recipientCount === RECIPIENT_COUNT_SINGLE && values.recipientType === RECIPIENT_TYPE_SELF) return false;
  return true;
};

export const customCardLogo = (productHandle: string) => {
  switch (productHandle) {
    case PRODUCT_HANDLE.MASTER_CARD_CUSTOM:
    case PRODUCT_HANDLE.MASTER_CARD:
      return mastercardLogo;
    case PRODUCT_HANDLE.EFTPOS:
    case PRODUCT_HANDLE.EFTPOS_CARD_CUSTOM:
      return eftposLogo;
    default:
      return undefined;
  }
};
